<template>
  <div>
    <div class="mt-5 d-flex align-center justify-space-between mb-6">
      <h2 class="text-h4 font-weight-bold">Reports</h2>
      <v-select
        v-model="selectedYear"
        :items="years"
        label="Select Fiscal Year"
        outlined
        hide-details
        prepend-inner-icon="mdi-calendar-range"
        class="flex-grow-0 text-body-1 font-weight-medium"
        style="width: 250px"
        background-color="white"
      ></v-select>
    </div>

    <v-row class="stretch-height">
      <!-- Row 1: Activity Analysis (Bar Chart) & Top Performers -->
      <v-col cols="12" md="8" class="d-flex">
        <v-card class="mb-6 fill-height d-flex flex-column" style="width: 100%">
          <v-card-title class="font-weight-bold">
            <v-icon left color="primary">mdi-chart-bar</v-icon>
            Activity Analysis
          </v-card-title>
          <v-card-text class="flex-grow-1">

            <v-alert
              border="left"
              colored-border
              color="info"
              elevation="1"
              class="mt-4 mb-4"
            >
              <div class="subtitle-2 font-weight-bold mb-1">Activity Distribution Overview</div>
              <div class="caption">
                This chart compares the volume of <strong>Approved</strong> vs. <strong>Rejected</strong> activities across different categories.
                It highlights areas where approval rates are high versus areas requiring more attention.
              </div>
            </v-alert>

            <div class="d-flex justify-center mb-4">
              <v-select
                v-model="selectedActivityGroup"
                :items="activityGroupNames"
                label="Select Activity Group"
                outlined
                dense
                hide-details
                style="max-width: 350px"
                class="mt-5 mb-3"
              ></v-select>
            </div>

            <div v-if="selectedActivityGroup && activityGroupCharts[selectedActivityGroup]">
              <apexchart
                type="bar"
                :height="activityGroupCharts[selectedActivityGroup].chartHeight"
                :options="activityGroupCharts[selectedActivityGroup].options"
                :series="activityGroupCharts[selectedActivityGroup].series"
              ></apexchart>
            </div>
            <div v-else class="text-center pa-8 grey--text">
              No data available
            </div>

          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" class="d-flex">
        <v-card class="mb-6 fill-height d-flex flex-column" style="width: 100%">
          <v-card-title class="font-weight-bold">
            <v-icon left color="amber darken-2">mdi-trophy</v-icon>
            Top Performers
          </v-card-title>
          <v-card-text>
            <v-skeleton-loader
              v-if="loading"
              type="list-item-avatar-three-line@5"
            ></v-skeleton-loader>
            <div v-else-if="topPerformers.length === 0" class="d-flex flex-column justify-center align-center fill-height pa-8 text-center" style="min-height: 200px">
                <v-icon size="64" color="grey lighten-3" class="mb-3">mdi-account-off-outline</v-icon>
                <div class="text-h6 grey--text text--darken-1 font-weight-bold mb-1">No Top Performers Found</div>
                <div class="text-body-2 grey--text" style="max-width: 250px">
                    There are no approved activities recorded for the selected fiscal year yet.
                </div>
            </div>
            <v-list two-line v-else>
              <v-list-item v-for="(instructor, index) in topPerformers" :key="index">
                <v-list-item-avatar color="primary lighten-2">
                  <!-- 
                    <span class="white--text font-weight-bold" style="font-size: 12px">
                      {{ instructor.initials }}
                    </span>
                  -->
                  <img :src="'https://my.muic.io/api' +
                  instructor.image
                  " />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">{{ instructor.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ instructor.division }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="d-flex flex-column align-end">
                  <span class="text-caption grey--text">{{ instructor.jobType }}</span>
                  <v-chip small color="success" class="mt-1 font-weight-bold" outlined>
                    {{ instructor.hours }} Jobs
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- Row 2: Job Status (Bar) & Downloads -->
      <v-col cols="12" md="8" class="d-flex">
        <v-card class="mb-6 fill-height d-flex flex-column" style="width: 100%">
          <v-card-title class="font-weight-bold">
            <v-icon left color="info">mdi-domain</v-icon>
            Job Status by Division
          </v-card-title>
          <v-card-text>
            <apexchart type="bar" height="450" :options="chartOptions" :series="series"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" class="d-flex">
        <v-card class="mb-6 fill-height d-flex flex-column" style="width: 100%">
          <v-card-title class="font-weight-bold">
            <v-icon left color="grey darken-1">mdi-file-download</v-icon>
            Report Downloads
          </v-card-title>
          <v-divider />
          <v-list>
            <v-list-item v-for="report in downloads" :key="report.title">
              <v-list-item-content>
                <v-list-item-title>{{ report.title }}</v-list-item-title>
                <v-list-item-subtitle>{{ report.updated }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-btn icon @click="notify(report.title)" :loading="downloadingAll">
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </v-list-item>
          </v-list>
          
          <v-divider class="my-2" />
          
          <!-- Report by Division -->
          <v-card-subtitle class="font-weight-bold pb-0">
            <v-icon small left color="primary">mdi-domain</v-icon>
            Report by Division
          </v-card-subtitle>
          <v-card-text class="pt-2">
            <v-select
              v-model="selectedDivision"
              :items="divisionOptions"
              label="Select Division"
              outlined
              dense
              hide-details
              class="mb-2"
            ></v-select>
            <v-btn 
              block 
              color="primary" 
              :disabled="!selectedDivision"
              :loading="downloadingDivision"
              @click="downloadDivisionExcel"
            >
              <v-icon left>mdi-download</v-icon>
              Download Division Report
            </v-btn>
          </v-card-text>
          
          <v-divider class="my-2" />
          
          <!-- Report by Activity Type -->
          <v-card-subtitle class="font-weight-bold pb-0">
            <v-icon small left color="success">mdi-briefcase</v-icon>
            Report by Activity Type
          </v-card-subtitle>
          <v-card-text class="pt-2">
            <v-select
              v-model="selectedActivityType"
              :items="activityTypeOptions"
              item-text="detail"
              item-value="id"
              label="Select Activity Type"
              outlined
              dense
              hide-details
              class="mb-2"
              :loading="loadingActivityTypes"
            ></v-select>
            <v-btn 
              block 
              color="success" 
              :disabled="!selectedActivityType"
              :loading="downloadingActivityType"
              @click="downloadActivityTypeExcel"
            >
              <v-icon left>mdi-download</v-icon>
              Download Activity Report
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="toast" timeout="2000" color="info" top right>
      {{ toastMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export default {
  name: 'ReportsView',
  data: () => ({
    selectedRange: 'Last 7 days',
    ranges: ['Last 7 days', 'Last 30 days', 'Last Quarter'],
    toast: false,
    toastMessage: '',
    topPerformers: [],
    downloads: [
      { title: 'Instructor Evaluation Scores', updated: 'Current evaluation period' },
    ],
    quality: [
      { name: 'Data freshness', value: 92, color: 'success' },
      { name: 'Completeness', value: 87, color: 'primary' },
      { name: 'Accuracy', value: 81, color: 'secondary' },
    ],
    series: [], // will be populated by updateChartData
    selectedYear: 2024,
    years: [2024, 2023],
    selectedDivision: null,
    divisionOptions: [],
    selectedActivityType: null,
    activityTypeOptions: [],
    loadingActivityTypes: false,
    downloadingAll: false,
    downloadingDivision: false,
    downloadingActivityType: false,
    
    // Activity Group Selection
    selectedActivityGroup: null,
    activityGroupNames: [],
    activityGroupCharts: {},

    // Activity Chart Data (Horizontal Bar) - kept for fallback
    activitySeries: [],
    activityChartOptions: {
      chart: {
        type: 'bar',
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: 'top',
          },
        }
      },
      dataLabels: {
        enabled: true,
        offsetX: -6,
        style: {
          fontSize: '12px',
          colors: ['#fff']
        }
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff']
      },
      xaxis: {
        categories: [],
      },
      colors: ['#4CAF50', '#F44336'], // Approved (Green), Rejected (Red)
      legend: {
        show: true,
        position: 'bottom'
      }
    },

    chartOptions: {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded'
        },
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: [], // will be populated
        title: {
          text: 'Division'
        }
      },
      yaxis: {
        title: {
          text: 'Number of Jobs'
        }
      },
      colors: ['#4CAF50', '#F44336', '#2196F3'], // Success (Green), Reject (Red), Pending (Blue)
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " jobs"
          }
        }
      }
    },
    // Real Data
    activityTypes: [],
    mainActivities: [],
    filteredActivities: [],
    lecturers: [],
    loading: false,
    
    // Mock Data Store
    chartDataStore: {
      2024: {
        topPerformers: [], // Populated dynamically now
        downloads: [
          { title: 'Instructor Evaluation Scores', updated: 'Current evaluation period' },
        ],
        quality: [
          { name: 'Data freshness', value: 92, color: 'success' },
          { name: 'Completeness', value: 87, color: 'primary' },
          { name: 'Accuracy', value: 81, color: 'secondary' },
        ],
        chart: {
          categories: ['IT', 'HR', 'Sales', 'Admin', 'Marketing'],
          series: [
            { name: 'Success', data: [45, 52, 38, 24, 33] },
            { name: 'Reject', data: [12, 8, 15, 5, 10] },
            { name: 'Pending', data: [20, 15, 25, 10, 18] }
          ]
        },
      },
      2023: {
        topPerformers: [],
        downloads: [
          { title: 'Instructor Evaluation Scores', updated: 'Current evaluation period' },
        ],
        quality: [
          { name: 'Data freshness', value: 100, color: 'success' },
          { name: 'Completeness', value: 95, color: 'primary' },
          { name: 'Accuracy', value: 88, color: 'secondary' },
        ],
        chart: {
          categories: ['IT', 'HR', 'Sales', 'Admin', 'Marketing'],
          series: [
            { name: 'Success', data: [35, 40, 30, 20, 28] },
            { name: 'Reject', data: [15, 10, 12, 8, 12] },
            { name: 'Pending', data: [25, 20, 22, 12, 20] }
          ]
        },
      }
    },
  }),
  created() {
    // Set selectedYear before mounted/watchers to avoid duplicate API calls
    this.getPeriods()
  },
  async mounted() {
    this.loading = true
    await Promise.all([
        this.fetchData(),
        this.fetchLecturers(),
        this.fetchActivityTypesForReport()
    ])
    // Use activity type data from fetchActivityTypesForReport for charts
    this.activityTypes = this.activityTypeOptions
    this.calculateTopPerformers()
    this.calculateActivityChart()
    this.calculateJobStatusByDivision()
    this.loading = false
  },
  watch: {
    async selectedYear() {
        this.loading = true
        await this.fetchData()
        // Refresh chart and performer calculations with new data
        this.calculateTopPerformers()
        this.calculateActivityChart()
        this.calculateJobStatusByDivision()
        this.loading = false
    }
  },
  methods: {
    updatePageData() {
       // Mock update logic removed/ignored as we use real data now
    },
    
    // PA Year Logic
    async getPeriods() {
        this.years = []
        let year = new Date().getFullYear() + 543
        // Generate last 5 years starting from current year
        for (let i = year; i > year - 5; i--) {
            this.years.push({ value: i, text: `PA ${i.toString()}` })
        }
        
        // Ensure selectedYear is one of the options
         if (!this.selectedYear || !this.years.find(y => y.value === this.selectedYear)) {
            this.selectedYear = this.years[0].value
         }
    },

    getPaDateRange(paYearBE) {
        const yearAD = paYearBE - 543
        return {
            start: this.$dayjs(`${yearAD - 1}-07-01`),
            end: this.$dayjs(`${yearAD}-06-30`)
        }
    },

    async fetchData() {
        const { start, end } = this.getPaDateRange(this.selectedYear);
        
        try {
            // Fetch Main Activities (activity types are fetched separately via fetchActivityTypesForReport)
             const resActivities = await this.$http.get(`/academic-service/main-activities/`);
             if (resActivities.status === 200) {
                 this.mainActivities = resActivities.data;
                 
                 // Filter by PA Year
                 this.filteredActivities = this.mainActivities.filter(a => {
                    if (!a.date_end) return false;
                    const endDate = this.$dayjs(a.date_end);
                    return endDate.isSameOrAfter(start, 'day') && endDate.isSameOrBefore(end, 'day');
                 });
             }

        } catch (error) {
            console.error("Error fetching data:", error);
        }
    },
    
    async fetchLecturers() {
        try {
            const res = await this.$http.get(`/teaching/contract/lecturer_list/?lecturer_type=full_time`);
            if (res.status === 200) {
                this.lecturers = res.data;

                // Extract unique divisions from lecturers, sorted A-Z
                const divisionSet = new Set()
                this.lecturers.forEach(lecturer => {
                    if (lecturer.department && lecturer.department.name_eng) {
                        divisionSet.add(lecturer.department.name_eng)
                    }
                })
                this.divisionOptions = Array.from(divisionSet).sort((a, b) => a.localeCompare(b))
            }
        } catch (error) {
            console.error("Error fetching lecturers:", error);
        }
    },

    calculateTopPerformers() {
        this.topPerformers = []; // Reset first to avoid stale data
        
        if (!this.lecturers.length || !this.filteredActivities.length) return;

        const lecturerPerformance = this.lecturers.map(lecturer => {
            // Filter activities for this lecturer that are APPROVED
            const approvedCount = this.filteredActivities.filter(act => 
                act.muic_account_aj && 
                act.muic_account_aj.username === lecturer.muic_account &&
                act.status === 'approve'
            ).length;

            return {
                ...lecturer,
                approvedCount
            };
        });

        // Sort by approvedCount descending
        lecturerPerformance.sort((a, b) => b.approvedCount - a.approvedCount);

        // Filter out those with 0 activities
        const activeLecturers = lecturerPerformance.filter(l => l.approvedCount > 0);

        // Take top 5
        const top5 = activeLecturers.slice(0, 5);

        // Map to UI format
        this.topPerformers = top5.map(l => ({
            image: l.img, // Or generate from name if preferred, currently using title as placeholder logic from mock
            initials: l.title.short_eng, // Or generate from name if preferred, currently using title as placeholder logic from mock
            name: `${l.title.short_thai} ${l.name_thai} ${l.surname_thai}`,
            division: l.department ? l.department.name_thai : '-',
            jobType: l.position_thai || '-',
            hours: l.approvedCount // Using approvedCount as the value
        }));
    },
    
    calculateActivityChart() {
        if (!this.activityTypes.length) return;

        // Group activity types by name_type
        const groups = {};
        this.activityTypes.forEach(t => {
            const groupName = t.name_type || 'Other';
            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(t);
        });

        // Store group names in specified order
        const preferredOrder = [
            'Service-Oriented activities',
            'Practice-Oriented activities',
            'Teaching-Oriented activities',
            'Others'
        ];
        const allGroupNames = Object.keys(groups);
        // Sort: preferred order first, then any remaining alphabetically
        this.activityGroupNames = [
            ...preferredOrder.filter(name => allGroupNames.includes(name)),
            ...allGroupNames.filter(name => !preferredOrder.includes(name)).sort((a, b) => a.localeCompare(b))
        ];

        // Default to first group (Service-Oriented activities)
        if (!this.selectedActivityGroup || !this.activityGroupNames.includes(this.selectedActivityGroup)) {
            this.selectedActivityGroup = this.activityGroupNames[0] || null;
        }

        // Build chart data per group
        const groupCharts = {};
        this.activityGroupNames.forEach(groupName => {
            const typesInGroup = groups[groupName];
            const categories = typesInGroup.map(t => t.detail || 'Unknown');
            const approvedCounts = new Array(categories.length).fill(0);
            const rejectedCounts = new Array(categories.length).fill(0);

            this.filteredActivities.forEach(act => {
                const typeIndex = typesInGroup.findIndex(t => t.id == act.activity_type);
                if (typeIndex !== -1) {
                    if (act.status === 'approve') {
                        approvedCounts[typeIndex]++;
                    } else if (act.status === 'reject') {
                        rejectedCounts[typeIndex]++;
                    }
                }
            });

            groupCharts[groupName] = {
                chartHeight: Math.max(300, categories.length * 55),
                options: {
                    chart: {
                        type: 'bar',
                        toolbar: { show: false },
                        fontFamily: "'Kanit', sans-serif"
                    },
                    plotOptions: {
                        bar: {
                            horizontal: true,
                            barHeight: '60%',
                            dataLabels: { position: 'top' },
                        }
                    },
                    grid: {
                        padding: { left: 10, right: 20 }
                    },
                    colors: ['#81C784', '#E57373'],
                    xaxis: { categories: categories },
                    dataLabels: {
                        enabled: true,
                        offsetX: -6,
                        style: {
                            fontSize: '12px',
                            colors: ['#555'],
                            fontFamily: "'Kanit', sans-serif"
                        }
                    },
                    stroke: { show: true, width: 1, colors: ['#fff'] },
                    legend: { show: true, position: 'bottom' }
                },
                series: [
                    { name: 'Approved', data: approvedCounts },
                    { name: 'Rejected', data: rejectedCounts }
                ]
            };
        });

        this.activityGroupCharts = groupCharts;
    },

    calculateJobStatusByDivision() {
        if (!this.lecturers.length) return;

        const divisionMap = {};

        this.lecturers.forEach(lecturer => {
            if (!lecturer.department || !lecturer.department.name_eng) return;
            
            // Remove 'Division' from name for cleaner display
            const divisionName = lecturer.department.name_eng.replace(/ Division/gi, '').trim();
            
            if (!divisionMap[divisionName]) {
                divisionMap[divisionName] = 0;
            }

            // Count approved activities for this lecturer
            // Ensure filteredActivities is populated
            if (this.filteredActivities && this.filteredActivities.length) {
                 const approvedCount = this.filteredActivities.filter(act => 
                    act.muic_account_aj && 
                    act.muic_account_aj.username === lecturer.muic_account &&
                    act.status === 'approve'
                ).length;
                
                divisionMap[divisionName] += approvedCount;
            }
        });

        const categories = Object.keys(divisionMap);
        const data = Object.values(divisionMap);

        this.chartOptions = {
            ...this.chartOptions,
            chart: {
                ...this.chartOptions.chart,
                fontFamily: "'Kanit', sans-serif"
            },
            colors: ['#81C784', '#E57373', '#90CAF9'], // Pastel Palette
            xaxis: {
                ...this.chartOptions.xaxis,
                categories: categories,
                labels: {
                    rotate: 0,
                    trim: false,
                    style: {
                        fontSize: '12px',
                        fontFamily: "'Kanit', sans-serif"
                    },
                    formatter: (val) => {
                        if (typeof val === 'string' && val.length > 15) {
                            return val.substring(0, 15) + '...'
                        }
                        return val
                    }
                }
            },
            plotOptions: {
                bar: {
                  horizontal: false,
                  columnWidth: '55%',
                  endingShape: 'rounded',
                  distributed: true // To color each bar differently if desired, or keep uniform. Keeping uniform for now based on series logic.
                },
            },
        };

        this.series = [{
            name: 'Approved Jobs',
            data: data
        }];
    },

    notify(title) {
      this.toastMessage = `${title} download started`
      this.toast = true
      this.downloadExcel(title)
    },

    downloadExcel() {
      this.downloadingAll = true

      try {
        // Build lecturer report data from real API data
        const reportData = this.lecturers.map(lecturer => {
          const username = lecturer.muic_account
          const division = lecturer.department ? lecturer.department.name_eng : '-'
          const instructorName = `${lecturer.title ? lecturer.title.short_eng : ''} ${lecturer.name || ''} ${lecturer.surname || ''}`.trim()

          // Find approved activities for this lecturer (already filtered by PA year)
          const lecturerActivities = this.filteredActivities.filter(
            act => act.muic_account_aj && act.muic_account_aj.username === username && act.status === 'approve'
          )

          // Sum total hours
          const totalHours = lecturerActivities.reduce(
            (sum, act) => sum + (parseFloat(act.point_activity) || 0), 0
          )

          return {
            name: instructorName,
            email: lecturer.email || '-',
            division,
            totalActivities: lecturerActivities.length,
            totalHours,
          }
        })

        // Sort by Division A-Z, then by Instructor Name A-Z
        reportData.sort((a, b) => {
          const divCompare = a.division.localeCompare(b.division)
          if (divCompare !== 0) return divCompare
          return a.name.localeCompare(b.name)
        })

        // Build row data with sequential numbering
        const data = reportData.map((item, index) => ({
          'No.': index + 1,
          'Instructor Name': item.name,
          'Email': item.email,
          'Division': item.division,
          'Total Activities': item.totalActivities,
          'Total Hours': item.totalHours,
        }))

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet([])

        // Row 1: Report title (merged A1:F1)
        const headerTitle = `Instructor Evaluation Scores Round :: PA${this.selectedYear}`
        XLSX.utils.sheet_add_aoa(worksheet, [[headerTitle]], { origin: 'A1' })

        // Merge cells A1:F1
        worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }]

        // Row 2: Column headers
        const headers = ['No.', 'Instructor Name', 'Email', 'Division', 'Total Activities', 'Total Hours']
        XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A2' })

        // Row 3+: Data
        XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A3', skipHeader: true })

        // Set column widths
        worksheet['!cols'] = [
          { wch: 5 },   // No.
          { wch: 35 },  // Instructor Name
          { wch: 30 },  // Email
          { wch: 35 },  // Division
          { wch: 18 },  // Total Activities
          { wch: 15 },  // Total Hours
        ]

        // Create workbook
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Scores')

        // === Sheet 2: Score_Detail ===
        const detailSheet = XLSX.utils.json_to_sheet([])

        // Build a lookup map: username -> lecturer info
        const lecturerMap = {}
        this.lecturers.forEach(lecturer => {
          lecturerMap[lecturer.muic_account] = {
            name: `${lecturer.title ? lecturer.title.short_eng : ''} ${lecturer.name || ''} ${lecturer.surname || ''}`.trim(),
            division: lecturer.department ? lecturer.department.name_eng : '-',
          }
        })

        // Get all approved activities sorted by instructor name then date
        const approvedActivities = this.filteredActivities
          .filter(act => act.status === 'approve')
          .sort((a, b) => {
            const nameA = a.muic_account_aj ? a.muic_account_aj.username : ''
            const nameB = b.muic_account_aj ? b.muic_account_aj.username : ''
            const nameCompare = nameA.localeCompare(nameB)
            if (nameCompare !== 0) return nameCompare
            return new Date(a.date_start) - new Date(b.date_start)
          })

        // Build detail rows
        const detailData = approvedActivities.map((act, index) => {
          const username = act.muic_account_aj ? act.muic_account_aj.username : '-'
          const lecturer = lecturerMap[username] || { name: username, division: '-' }

          // Format activity type: append activity_other for "Others"
          let activityTypeLabel = act.activity_type_detail ? act.activity_type_detail.detail : '-'
          if (activityTypeLabel === 'Others' && act.activity_other && act.activity_other !== 'null' && String(act.activity_other).trim() !== '') {
            activityTypeLabel = `Others :: ${act.activity_other}`
          }

          return {
            'No.': index + 1,
            'Instructor Name': lecturer.name,
            'Division': lecturer.division,
            'Activity Name': act.name_academic || '-',
            'Activity Type': activityTypeLabel,
            'Organizer': act.organizer_record || '-',
            'Sector': act.sector || '-',
            'Status': act.status || '-',
            'Remark': act.status_remark || '-',
            'Date Start': act.date_start ? this.$dayjs(act.date_start).format('DD/MM/YYYY') : '-',
            'Date End': act.date_end ? this.$dayjs(act.date_end).format('DD/MM/YYYY') : '-',
            'Hours': parseFloat(act.point_activity) || 0,
            'Points': parseFloat(act.point_activity) || 0,
          }
        })

        // Row 1: Title (merged A1:M1)
        const detailTitle = `Score Detail :: PA${this.selectedYear}`
        XLSX.utils.sheet_add_aoa(detailSheet, [[detailTitle]], { origin: 'A1' })
        detailSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }]

        // Row 2: Column headers
        const detailHeaders = ['No.', 'Instructor Name', 'Division', 'Activity Name', 'Activity Type', 'Organizer', 'Sector', 'Status', 'Remark', 'Date Start', 'Date End', 'Hours', 'Points']
        XLSX.utils.sheet_add_aoa(detailSheet, [detailHeaders], { origin: 'A2' })

        // Row 3+: Data
        XLSX.utils.sheet_add_json(detailSheet, detailData, { origin: 'A3', skipHeader: true })

        // Set column widths
        detailSheet['!cols'] = [
          { wch: 5 },   // No.
          { wch: 35 },  // Instructor Name
          { wch: 35 },  // Division
          { wch: 40 },  // Activity Name
          { wch: 35 },  // Activity Type
          { wch: 20 },  // Organizer
          { wch: 15 },  // Sector
          { wch: 12 },  // Status
          { wch: 25 },  // Remark
          { wch: 15 },  // Date Start
          { wch: 15 },  // Date End
          { wch: 12 },  // Hours
          { wch: 12 },  // Points
        ]

        XLSX.utils.book_append_sheet(workbook, detailSheet, 'Score_Detail')

        const fileName = `Instructor_Evaluation_Scores_PA${this.selectedYear}.xlsx`

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, fileName)
      } catch (error) {
        console.error('Error downloading Excel:', error)
      } finally {
        this.downloadingAll = false
      }
    },

    downloadDivisionExcel() {
      if (!this.selectedDivision) return

      this.downloadingDivision = true

      try {
        // Filter lecturers by selected division
        const divisionLecturers = this.lecturers.filter(
          lecturer => lecturer.department && lecturer.department.name_eng === this.selectedDivision
        )

        // Build report data for each lecturer in the division
        const reportData = divisionLecturers.map(lecturer => {
          const username = lecturer.muic_account
          const instructorName = `${lecturer.title ? lecturer.title.short_eng : ''} ${lecturer.name || ''} ${lecturer.surname || ''}`.trim()

          // Find approved activities for this lecturer (already filtered by PA year)
          const lecturerActivities = this.filteredActivities.filter(
            act => act.muic_account_aj && act.muic_account_aj.username === username && act.status === 'approve'
          )

          const totalHours = lecturerActivities.reduce(
            (sum, act) => sum + (parseFloat(act.point_activity) || 0), 0
          )

          return {
            name: instructorName,
            email: lecturer.email || '-',
            division: this.selectedDivision,
            totalActivities: lecturerActivities.length,
            totalHours,
          }
        })

        // Sort by Instructor Name A-Z
        reportData.sort((a, b) => a.name.localeCompare(b.name))

        // Build row data with sequential numbering
        const data = reportData.map((item, index) => ({
          'No.': index + 1,
          'Instructor Name': item.name,
          'Email': item.email,
          'Division': item.division,
          'Total Activities': item.totalActivities,
          'Total Hours': item.totalHours,
        }))

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet([])

        // Row 1: Report title (merged A1:F1)
        const headerTitle = `Instructor Division ${this.selectedDivision} Report :: PA${this.selectedYear}`
        XLSX.utils.sheet_add_aoa(worksheet, [[headerTitle]], { origin: 'A1' })

        // Merge cells A1:F1
        worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }]

        // Row 2: Column headers
        const headers = ['No.', 'Instructor Name', 'Email', 'Division', 'Total Activities', 'Total Hours']
        XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A2' })

        // Row 3+: Data
        XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A3', skipHeader: true })

        // Set column widths
        worksheet['!cols'] = [
          { wch: 5 },   // No.
          { wch: 35 },  // Instructor Name
          { wch: 30 },  // Email
          { wch: 35 },  // Division
          { wch: 18 },  // Total Activities
          { wch: 15 },  // Total Hours
        ]

        // Create workbook
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Division_Report')

        // === Sheet 2: Division_Report_Detail ===
        const detailSheet = XLSX.utils.json_to_sheet([])

        // Build a lookup map: username -> lecturer info (only for this division)
        const lecturerMap = {}
        divisionLecturers.forEach(lecturer => {
          lecturerMap[lecturer.muic_account] = {
            name: `${lecturer.title ? lecturer.title.short_eng : ''} ${lecturer.name || ''} ${lecturer.surname || ''}`.trim(),
            division: lecturer.department ? lecturer.department.name_eng : '-',
          }
        })

        // Get approved activities for this division's lecturers, sorted by instructor then date
        const divisionUsernames = new Set(divisionLecturers.map(l => l.muic_account))
        const approvedActivities = this.filteredActivities
          .filter(act =>
            act.status === 'approve' &&
            act.muic_account_aj &&
            divisionUsernames.has(act.muic_account_aj.username)
          )
          .sort((a, b) => {
            const nameA = a.muic_account_aj ? a.muic_account_aj.username : ''
            const nameB = b.muic_account_aj ? b.muic_account_aj.username : ''
            const nameCompare = nameA.localeCompare(nameB)
            if (nameCompare !== 0) return nameCompare
            return new Date(a.date_start) - new Date(b.date_start)
          })

        // Build detail rows
        const detailData = approvedActivities.map((act, index) => {
          const username = act.muic_account_aj ? act.muic_account_aj.username : '-'
          const lecturer = lecturerMap[username] || { name: username, division: '-' }

          // Format activity type: append activity_other for "Others"
          let activityTypeLabel = act.activity_type_detail ? act.activity_type_detail.detail : '-'
          if (activityTypeLabel === 'Others' && act.activity_other && act.activity_other !== 'null' && String(act.activity_other).trim() !== '') {
            activityTypeLabel = `Others :: ${act.activity_other}`
          }

          return {
            'No.': index + 1,
            'Instructor Name': lecturer.name,
            'Division': lecturer.division,
            'Activity Name': act.name_academic || '-',
            'Activity Type': activityTypeLabel,
            'Organizer': act.organizer_record || '-',
            'Sector': act.sector || '-',
            'Status': act.status || '-',
            'Remark': act.status_remark || '-',
            'Date Start': act.date_start ? this.$dayjs(act.date_start).format('DD/MM/YYYY') : '-',
            'Date End': act.date_end ? this.$dayjs(act.date_end).format('DD/MM/YYYY') : '-',
            'Hours': parseFloat(act.point_activity) || 0,
            'Points': parseFloat(act.point_activity) || 0,
          }
        })

        // Row 1: Title (merged A1:M1)
        const detailTitle = `Division Report Detail :: ${this.selectedDivision} :: PA${this.selectedYear}`
        XLSX.utils.sheet_add_aoa(detailSheet, [[detailTitle]], { origin: 'A1' })
        detailSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }]

        // Row 2: Column headers
        const detailHeaders = ['No.', 'Instructor Name', 'Division', 'Activity Name', 'Activity Type', 'Organizer', 'Sector', 'Status', 'Remark', 'Date Start', 'Date End', 'Hours', 'Points']
        XLSX.utils.sheet_add_aoa(detailSheet, [detailHeaders], { origin: 'A2' })

        // Row 3+: Data
        XLSX.utils.sheet_add_json(detailSheet, detailData, { origin: 'A3', skipHeader: true })

        // Set column widths
        detailSheet['!cols'] = [
          { wch: 5 },   // No.
          { wch: 35 },  // Instructor Name
          { wch: 35 },  // Division
          { wch: 40 },  // Activity Name
          { wch: 35 },  // Activity Type
          { wch: 20 },  // Organizer
          { wch: 15 },  // Sector
          { wch: 12 },  // Status
          { wch: 25 },  // Remark
          { wch: 15 },  // Date Start
          { wch: 15 },  // Date End
          { wch: 12 },  // Hours
          { wch: 12 },  // Points
        ]

        XLSX.utils.book_append_sheet(workbook, detailSheet, 'Division_Report_Detail')

        const divisionName = this.selectedDivision.replace(/\s+/g, '_')
        const fileName = `Instructor_Division_${divisionName}_PA${this.selectedYear}.xlsx`

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, fileName)

        // Show toast and clear selection
        this.toastMessage = `${this.selectedDivision} report download started`
        this.toast = true
        this.selectedDivision = null
      } catch (error) {
        console.error('Error downloading Division Excel:', error)
      } finally {
        this.downloadingDivision = false
      }
    },

    async fetchActivityTypesForReport() {
      this.loadingActivityTypes = true
      try {
        const res = await this.$http.get('/academic-service/activity-types/')
        if (res.status === 200) {
          // Filter only active activity types for the report dropdown
          this.activityTypeOptions = res.data.filter(item => item.status === 'on')
          // Also populate activityTypes for chart calculations
          this.activityTypes = res.data
        }
      } catch (error) {
        console.error('Error fetching activity types:', error)
      } finally {
        this.loadingActivityTypes = false
      }
    },

    async downloadActivityTypeExcel() {
      if (!this.selectedActivityType) return

      this.downloadingActivityType = true

      try {
        // Find selected activity type
        const selectedType = this.activityTypeOptions.find(t => t.id === this.selectedActivityType)
        if (!selectedType) {
          this.downloadingActivityType = false
          return
        }

        const activityTypeName = selectedType.detail

        // Filter approved activities matching the selected activity type (already filtered by PA year)
        const typeActivities = this.filteredActivities.filter(
          act => act.status === 'approve' && act.activity_type === this.selectedActivityType
        )

        // Build a lookup map: username -> lecturer info
        const lecturerMap = {}
        this.lecturers.forEach(lecturer => {
          lecturerMap[lecturer.muic_account] = {
            name: `${lecturer.title ? lecturer.title.short_eng : ''} ${lecturer.name || ''} ${lecturer.surname || ''}`.trim(),
            email: lecturer.email || '-',
            division: lecturer.department ? lecturer.department.name_eng : '-',
          }
        })

        // Group activities by lecturer and build summary data
        const lecturerSummary = {}
        typeActivities.forEach(act => {
          const username = act.muic_account_aj ? act.muic_account_aj.username : null
          if (!username) return

          if (!lecturerSummary[username]) {
            const lecturer = lecturerMap[username] || { name: username, email: '-', division: '-' }
            lecturerSummary[username] = {
              name: lecturer.name,
              email: lecturer.email,
              division: lecturer.division,
              totalActivities: 0,
              totalHours: 0,
            }
          }

          lecturerSummary[username].totalActivities += 1
          lecturerSummary[username].totalHours += parseFloat(act.point_activity) || 0
        })

        // Convert to array and sort by Division A-Z, then Name A-Z
        const reportData = Object.values(lecturerSummary).sort((a, b) => {
          const divCompare = a.division.localeCompare(b.division)
          if (divCompare !== 0) return divCompare
          return a.name.localeCompare(b.name)
        })

        // Build row data with sequential numbering
        const data = reportData.map((item, index) => ({
          'No.': index + 1,
          'Instructor Name': item.name,
          'Email': item.email,
          'Division': item.division,
          'Total Activities': item.totalActivities,
          'Total Hours': item.totalHours,
        }))

        // === Sheet 1: Activity_Type_Report ===
        const worksheet = XLSX.utils.json_to_sheet([])

        // Row 1: Report title (merged A1:F1)
        const headerTitle = `Activity Type :: ${activityTypeName} Report :: PA${this.selectedYear}`
        XLSX.utils.sheet_add_aoa(worksheet, [[headerTitle]], { origin: 'A1' })

        // Merge cells A1:F1
        worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }]

        // Row 2: Column headers
        const headers = ['No.', 'Instructor Name', 'Email', 'Division', 'Total Activities', 'Total Hours']
        XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A2' })

        // Row 3+: Data
        XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A3', skipHeader: true })

        // Set column widths
        worksheet['!cols'] = [
          { wch: 5 },   // No.
          { wch: 35 },  // Instructor Name
          { wch: 30 },  // Email
          { wch: 35 },  // Division
          { wch: 18 },  // Total Activities
          { wch: 15 },  // Total Hours
        ]

        // Create workbook
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Activity_Type_Report')

        // === Sheet 2: Activity_Type_Detail ===
        const detailSheet = XLSX.utils.json_to_sheet([])

        // Sort activities by instructor then date
        const sortedActivities = [...typeActivities].sort((a, b) => {
          const nameA = a.muic_account_aj ? a.muic_account_aj.username : ''
          const nameB = b.muic_account_aj ? b.muic_account_aj.username : ''
          const nameCompare = nameA.localeCompare(nameB)
          if (nameCompare !== 0) return nameCompare
          return new Date(a.date_start) - new Date(b.date_start)
        })

        // Build detail rows
        const detailData = sortedActivities.map((act, index) => {
          const username = act.muic_account_aj ? act.muic_account_aj.username : '-'
          const lecturer = lecturerMap[username] || { name: username, division: '-' }

          // Format activity type: append activity_other for "Others"
          let activityTypeLabel = act.activity_type_detail ? act.activity_type_detail.detail : '-'
          if (activityTypeLabel === 'Others' && act.activity_other && act.activity_other !== 'null' && String(act.activity_other).trim() !== '') {
            activityTypeLabel = `Others :: ${act.activity_other}`
          }

          return {
            'No.': index + 1,
            'Instructor Name': lecturer.name,
            'Division': lecturer.division,
            'Activity Name': act.name_academic || '-',
            'Activity Type': activityTypeLabel,
            'Organizer': act.organizer_record || '-',
            'Sector': act.sector || '-',
            'Status': act.status || '-',
            'Remark': act.status_remark || '-',
            'Date Start': act.date_start ? this.$dayjs(act.date_start).format('DD/MM/YYYY') : '-',
            'Date End': act.date_end ? this.$dayjs(act.date_end).format('DD/MM/YYYY') : '-',
            'Hours': parseFloat(act.point_activity) || 0,
            'Points': parseFloat(act.point_activity) || 0,
          }
        })

        // Row 1: Title (merged A1:M1)
        const detailTitle = `Activity Type Detail :: ${activityTypeName} :: PA${this.selectedYear}`
        XLSX.utils.sheet_add_aoa(detailSheet, [[detailTitle]], { origin: 'A1' })
        detailSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }]

        // Row 2: Column headers
        const detailHeaders = ['No.', 'Instructor Name', 'Division', 'Activity Name', 'Activity Type', 'Organizer', 'Sector', 'Status', 'Remark', 'Date Start', 'Date End', 'Hours', 'Points']
        XLSX.utils.sheet_add_aoa(detailSheet, [detailHeaders], { origin: 'A2' })

        // Row 3+: Data
        XLSX.utils.sheet_add_json(detailSheet, detailData, { origin: 'A3', skipHeader: true })

        // Set column widths
        detailSheet['!cols'] = [
          { wch: 5 },   // No.
          { wch: 35 },  // Instructor Name
          { wch: 35 },  // Division
          { wch: 40 },  // Activity Name
          { wch: 35 },  // Activity Type
          { wch: 20 },  // Organizer
          { wch: 15 },  // Sector
          { wch: 12 },  // Status
          { wch: 25 },  // Remark
          { wch: 15 },  // Date Start
          { wch: 15 },  // Date End
          { wch: 12 },  // Hours
          { wch: 12 },  // Points
        ]

        XLSX.utils.book_append_sheet(workbook, detailSheet, 'Activity_Type_Detail')

        // Export
        const typeName = activityTypeName.replace(/\s+/g, '_')
        const fileName = `Activity_Type_${typeName}_PA${this.selectedYear}.xlsx`

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, fileName)

        // Show toast and clear selection
        this.toastMessage = `${activityTypeName} report download started`
        this.toast = true
        this.selectedActivityType = null
      } catch (error) {
        console.error('Error downloading Activity Type Excel:', error)
      } finally {
        this.downloadingActivityType = false
      }
    },
  },
}
</script>
